import * as React from "react";
import {
  CheckCircle,
  ExclamationCircle,
  GeoAltFill,
} from "react-bootstrap-icons";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import { Helmet } from "react-helmet-async";
import Nav from "react-bootstrap/Nav";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Tab from "react-bootstrap/Tab";
import Tooltip from "react-bootstrap/Tooltip";

import { Api, PageSpinner, formatDate, useApi } from "../util";
import { AppEvent, AppInvite } from "../types";
import AddToCalendarButton from "../components/AddToCalendarButton";
import { AuthContext } from "../context";
import EventDescription from "../components/EventDescription";

import "../styles/rsvp.css";
import proposal from "../img/proposal.webp";

const EventHeader = ({
  event,
  showEventName = true,
}: {
  event: AppEvent;
  showEventName?: boolean;
}) => {
  return (
    <>
      <div className="pb-2">
        {showEventName && <h1 className="tagline pb-2">{event.name}</h1>}
        <h4 className="text-nowrap">
          {formatDate(event.start_date)} <AddToCalendarButton event={event} />
        </h4>
        <h5>
          {event.venue}{" "}
          <a
            className="text-decoration-none"
            href={event.venue_link}
            rel="noreferrer"
            target="_blank"
          >
            <GeoAltFill className="align-text-top" />
          </a>
        </h5>
      </div>
      <div className="fw-lighter">
        <EventDescription detailed={false} event={event} />
      </div>
    </>
  );
};

const InviteDisplay = ({
  invite,
  setInvite,
  api,
  saveHint,
  showEventName = true,
}: {
  invite: AppInvite;
  setInvite: (invite: AppInvite) => void;
  api: Api;
  saveHint: string;
  showEventName?: boolean;
}) => {
  const { event, responses } = invite;

  const [attending, setAttending] = React.useState<
    Record<number, number | null>
  >(Object.fromEntries(responses.map((r) => [r.guest.id, r.attending])));
  const [saving, setSaving] = React.useState(false);

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (event.currentTarget.reportValidity()) {
        void (async () => {
          setSaving(true);
          setInvite(await api.updateInvite(invite.id, attending));
          setSaving(false);
        })();
      }
    },
    [attending, api, invite.id, setInvite]
  );

  return (
    <Form onSubmit={handleSubmit}>
      <EventHeader event={event} showEventName={showEventName} />
      <hr />
      <div className="d-flex gap-2 flex-column pb-3">
        {responses.map(({ guest, mode }) => (
          <div
            className="d-flex justify-content-between align-items-center"
            key={guest.id}
          >
            {guest.name}
            {mode === "group" ? (
              <FloatingLabel
                className="text-dark"
                controlId="guest-count"
                label="Guest count"
              >
                <Form.Control
                  defaultValue={attending[guest.id] ?? undefined}
                  onChange={(e) =>
                    setAttending({
                      ...attending,
                      [guest.id]: parseInt(e.target.value, 10),
                    })
                  }
                  placeholder="Guest count"
                  required
                  size="sm"
                  type="number"
                />
              </FloatingLabel>
            ) : (
              <ButtonGroup>
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      The deadline to Accept has passed. Please contact us if
                      you have a change of plans.
                    </Tooltip>
                  }
                  placement={"top"}
                >
                  <span>
                    <Button
                      active={attending[guest.id] === 1}
                      className="rsvp-button"
                      onClick={() =>
                        setAttending({ ...attending, [guest.id]: 1 })
                      }
                      style={{ pointerEvents: "none" }}
                    >
                      Accept
                    </Button>
                  </span>
                </OverlayTrigger>
                <Button
                  active={attending[guest.id] === 0}
                  className="rsvp-button"
                  onClick={() => setAttending({ ...attending, [guest.id]: 0 })}
                >
                  Decline
                </Button>
              </ButtonGroup>
            )}
          </div>
        ))}
      </div>
      <Row>
        <Col sm={2} xs={12}>
          <Button disabled={saving} type="submit">
            {saving ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Col>
        <Col className="text-white-50 pt-2 pt-sm-0 text-sm-end" sm={10} xs={12}>
          {saveHint}
        </Col>
      </Row>
    </Form>
  );
};

const RsvpLanding = ({ invitesLength }: { invitesLength: number }) => (
  <div className="text-center d-flex align-items-center flex-column fs-4 gap-4">
    <div className="pb-4 fw-lighter">
      The Sharma and Cherivirala families request the pleasure of your company
      to celebrate the wedding&nbsp;of
    </div>
    <div className="tagline text-nowrap">
      <div style={{ lineHeight: 0.6 }}>Snigdha Sharma</div> <div>&amp;</div>{" "}
      <div>Sushain Cherivirala</div>
    </div>
    <div className="fs-5 fw-lighter">
      Please RSVP {invitesLength > 1 ? "to each event above" : ""} by March 15,
      2023.
    </div>
  </div>
);

const InviteNav = ({
  invites,
  showStatus,
}: {
  invites: Array<AppInvite>;
  showStatus: boolean;
}) => {
  return (
    <Nav className="d-flex justify-content-center" variant="pills">
      {invites.map((invite) => (
        <Nav.Item key={invite.event.slug}>
          <Nav.Link
            className="d-flex align-items-center text-uppercase gap-2"
            eventKey={invite.event.slug}
          >
            {showStatus ? (
              invite.responses.filter((r) => r.attending == null).length > 0 ? (
                <ExclamationCircle color="#eca2a9" />
              ) : (
                <CheckCircle color="#d1e7dd" />
              )
            ) : null}{" "}
            {invite.event.name}
          </Nav.Link>
        </Nav.Item>
      ))}
    </Nav>
  );
};

const Rsvp = () => {
  const [loading, setLoading] = React.useState(true);
  const [invites, setInvites] = React.useState<Array<AppInvite>>();
  const [showSaveHint, setShowSaveHint] = React.useState(false);

  const { fetch } = React.useContext(AuthContext);
  const api = useApi(fetch!);

  React.useEffect(() => {
    void (async () => {
      const invites = await api.getInvites();
      invites.sort((a, b) => a.event.start_date - b.event.start_date);
      setInvites(invites);

      setLoading(false);
    })();
  }, [api]);

  const eventSlug = useParams()["*"];
  const navigate = useNavigate();

  if (loading || !invites) {
    return <PageSpinner />;
  }

  if (eventSlug && !invites.some((i) => i.event.slug === eventSlug)) {
    navigate("/rsvp");
  }

  const rsvpsComplete = invites.filter(({ responses }) =>
    responses.every(({ attending }) => attending != null)
  ).length;
  let saveHint = `${rsvpsComplete}/${invites.length} RSVPs complete`;
  if (invites.length === 1 && rsvpsComplete !== 1) {
    saveHint = "";
  } else if (rsvpsComplete === invites.length) {
    saveHint = `RSVP${invites.length !== 1 ? "s" : ""} complete, thank you!`;
  }

  if (invites.length === 1) {
    return (
      <Container className="rsvp text-light" fluid>
        <Helmet>
          <title>RSVP</title>
        </Helmet>
        <Row className="d-flex justify-content-center">
          <Col className="pt-2" sm={6} xs={12}>
            <RsvpLanding invitesLength={invites.length} />
            <hr />
          </Col>
        </Row>
        <Row className="d-flex justify-content-center">
          <Col className="pt-3 pt-md-0" md={6} sm={4} xl={4}>
            <InviteDisplay
              api={api}
              invite={invites[0]}
              saveHint={showSaveHint ? saveHint : ""}
              setInvite={(invite: AppInvite) => {
                setInvites([invite]);
                setShowSaveHint(true);
              }}
              showEventName={false}
            />
          </Col>
          <Col sm={2} xs={7}>
            <img className="w-100 p-1" src={proposal} />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="rsvp text-light" fluid>
      <Helmet>
        <title>RSVP</title>
      </Helmet>
      <Tab.Container
        activeKey={eventSlug}
        onSelect={(slug: string | null) =>
          navigate(slug ? `/rsvp/${slug}` : "/rsvp")
        }
      >
        <Row className="pb-md-3">
          <InviteNav
            invites={invites}
            showStatus={!(eventSlug === "" || eventSlug == null)}
          />
        </Row>
        <Row className="d-flex justify-content-center">
          <Routes>
            <Route
              element={
                <>
                  <Col className="pt-3" sm={3} xs={8}>
                    <img className="w-100 p-3" src={proposal} />
                  </Col>
                  <Col className="pt-2" sm={5} xs={12}>
                    <RsvpLanding invitesLength={invites.length} />
                  </Col>
                </>
              }
              index
            />
            <Route
              element={
                <>
                  <Col className="pt-3 pt-md-0" md={6} sm={4} xl={4}>
                    {invites.map((invite, i) => (
                      <Tab.Content key={invite.event.slug}>
                        <Tab.Pane eventKey={invite.event.slug}>
                          <InviteDisplay
                            api={api}
                            invite={invite}
                            saveHint={showSaveHint ? saveHint : ""}
                            setInvite={(invite: AppInvite) => {
                              setInvites(
                                invites.map((originalInvite, j) =>
                                  i === j ? invite : originalInvite
                                )
                              );
                              setShowSaveHint(true);
                            }}
                          />
                        </Tab.Pane>
                      </Tab.Content>
                    ))}
                  </Col>
                  <Col sm={2} xs={7}>
                    <img className="w-100 p-1" src={proposal} />
                  </Col>
                </>
              }
              path=":eventSlug"
            />
          </Routes>
        </Row>
      </Tab.Container>
    </Container>
  );
};

export default Rsvp;
